/**
 * Top Bar Component
 * Responsive header with mobile menu toggle
 */

import React from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlass, 
  Bell, 
  Plus,
  CaretDown,
  Question,
  User,
  SignOut,
  Users,
  Gear,
  List,
  X,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const pageTitles = {
  '/': 'Aperçu',
  '/pipeline': 'Pipeline',
  '/deal': 'Détail Deal',
  '/analyzer': 'Analyseur',
  '/renovation': 'Rénovation',
  '/comparables': 'Comparables',
  '/exit': 'Suivi Sortie',
  '/partners': 'Partenaires',
  '/settings': 'Paramètres',
  '/help': 'Aide',
  '/users': 'Utilisateurs',
  '/profile': 'Mon Compte',
  '/data-ingestion': 'Données',
};

// User Avatar component
const UserAvatar = ({ initials, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
  };
  
  return (
    <div 
      className={cn(
        'rounded-full flex items-center justify-center font-medium bg-[#3A4A40] text-white flex-shrink-0',
        sizeClasses[size]
      )}
    >
      {initials || '?'}
    </div>
  );
};

export const TopBar = ({ 
  stats, 
  user, 
  onAddDeal, 
  onAddComparable, 
  onAddPartner,
  onMobileMenuToggle,
  isMobileMenuOpen 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAdmin } = useAuth();
  
  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.startsWith('/deal/') && path.includes('/edit')) return 'Modifier Deal';
    if (path.startsWith('/deal/new')) return 'Nouveau Deal';
    if (path.startsWith('/deal/')) return 'Deal';
    return pageTitles[path] || 'Tableau de Bord';
  };

  const handleAddDeal = () => {
    if (onAddDeal) {
      onAddDeal();
    } else {
      navigate('/deal/new');
    }
  };

  const handleAddComparable = () => {
    if (onAddComparable) {
      onAddComparable();
    } else {
      navigate('/comparables?action=add');
    }
  };

  const handleAddPartner = () => {
    if (onAddPartner) {
      onAddPartner();
    } else {
      navigate('/partners?action=add');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get user initials
  const userInitials = user?.full_name 
    ? user.full_name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : user?.initials || '?';

  return (
    <header 
      data-testid="topbar"
      className="sticky top-0 h-14 sm:h-16 bg-white border-b border-border flex items-center justify-between px-3 sm:px-6 z-40"
    >
      {/* Left: Mobile menu + Page Title */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        {/* Mobile menu toggle */}
        <button
          onClick={onMobileMenuToggle}
          className="p-2 rounded-lg hover:bg-secondary transition-colors lg:hidden flex-shrink-0"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          data-testid="mobile-menu-btn"
        >
          {isMobileMenuOpen ? <X size={22} /> : <List size={22} />}
        </button>
        
        <h1 className="text-base sm:text-xl font-semibold text-foreground truncate">
          {getPageTitle()}
        </h1>
      </div>

      {/* Center: Quick Stats - Hidden on mobile */}
      {stats && (
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          <div className="text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Deals</p>
            <p className="text-base font-semibold font-tabular">{stats.activeDeals || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Capital</p>
            <p className="text-base font-semibold font-tabular">{stats.capitalDeployed || '—'}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Rendement</p>
            <p className="text-base font-semibold font-tabular text-[#2E5F43]">{stats.avgRoi || '—'}</p>
          </div>
        </div>
      )}

      {/* Right: Actions */}
      <div className="flex items-center gap-1 sm:gap-3">
        {/* Search - Hidden on small mobile */}
        <button 
          data-testid="search-btn"
          className="hidden sm:flex p-2 rounded-lg hover:bg-secondary transition-colors"
        >
          <MagnifyingGlass size={20} className="text-muted-foreground" />
        </button>

        {/* Notifications */}
        <button 
          data-testid="notifications-btn"
          className="p-2 rounded-lg hover:bg-secondary transition-colors relative"
        >
          <Bell size={20} className="text-muted-foreground" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#B65B46] rounded-full" />
        </button>

        {/* Help - Hidden on mobile */}
        <Link 
          to="/help"
          data-testid="help-btn"
          className="hidden sm:flex p-2 rounded-lg hover:bg-secondary transition-colors"
          title="Help & Documentation"
        >
          <Question size={20} className="text-muted-foreground" />
        </Link>

        {/* Add New Dropdown - Compact on mobile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              data-testid="add-new-btn"
              className="bg-[#3A4A40] hover:bg-[#2E3B33] text-white h-9 px-2 sm:px-4"
              size="sm"
            >
              <Plus size={18} />
              <span className="hidden sm:inline ml-1">Ajouter</span>
              <CaretDown size={14} className="ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem 
              data-testid="add-deal-menu-item"
              onClick={handleAddDeal}
              className="cursor-pointer"
            >
              Nouveau Deal
            </DropdownMenuItem>
            <DropdownMenuItem 
              data-testid="add-comparable-menu-item"
              onClick={handleAddComparable}
              className="cursor-pointer"
            >
              Nouveau Comparable
            </DropdownMenuItem>
            <DropdownMenuItem 
              data-testid="add-partner-menu-item"
              onClick={handleAddPartner}
              className="cursor-pointer"
            >
              Nouveau Partenaire
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              data-testid="user-menu-btn"
              className="flex items-center gap-1 p-1 rounded-lg hover:bg-secondary transition-colors"
            >
              <UserAvatar initials={userInitials} />
              <CaretDown size={14} className="text-muted-foreground hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-3 py-2 border-b">
              <p className="font-medium text-sm truncate">{user?.full_name || 'Utilisateur'}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-secondary">
                {user?.role || 'utilisateur'}
              </span>
            </div>
            <DropdownMenuItem 
              data-testid="profile-menu-item"
              onClick={() => navigate('/profile')}
              className="cursor-pointer gap-2"
            >
              <User size={16} />
              Mon Compte
            </DropdownMenuItem>
            <DropdownMenuItem 
              data-testid="settings-menu-item"
              onClick={() => navigate('/settings')}
              className="cursor-pointer gap-2"
            >
              <Gear size={16} />
              Paramètres
            </DropdownMenuItem>
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  data-testid="users-menu-item"
                  onClick={() => navigate('/users')}
                  className="cursor-pointer gap-2"
                >
                  <Users size={16} />
                  Gestion Utilisateurs
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              data-testid="logout-menu-item"
              onClick={handleLogout}
              className="cursor-pointer gap-2 text-red-600 focus:text-red-600"
            >
              <SignOut size={16} />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default TopBar;
